import { getProductMainData, getReviewProductData } from '@entities/Product';
import { configProduct } from '@entities/Product/module/const/config';
import { sortTabs } from '@entities/Product/module/helper/sortTabs';
import { getCountReviews } from '@entities/Product/module/services/ProductGetCountReviews';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';
import { attachFeatureToProduct } from '../helper/attachFeatureToProduct';
import { generateTabs } from '../helper/generateTabs';
import { getDefaultQuantityByType, isEquipment } from '../helper/getDefaultQuantityByType';
import { Product, ProductSchema, ProductSpecCode, ProductSpecs, TabTitle, TabType, mediaType } from '../types/productSchema';

const initialState: ProductSchema = {
  product: null,
  activeTab: TabTitle.Availability,
  hideShopTwoHours: false,
  isLoadedData: false,
  reviewProduct: null,
  tabs: [],
};

export const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    setSpecProduct: (state, action: PayloadAction<ProductSpecs>) => {
      state.product.productSpecs = [...state.product.productSpecs].map(spec => {
        if (spec.code === action.payload.code) {
          return { ...action.payload };
        } else {
          return { ...spec };
        }
      });
    },
    initProduct: (state, action: PayloadAction<Product>) => {
      state.product = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<number>) => {
      const active = state.tabs[action.payload];
      if (active) {
        state.activeTab = active.title;
      }
    },
    setCloseMobileTab: state => {
      state.activeTab = null;
    },
    incrementQuantity: state => {
      state.product.actualQuantity += state.product.actualQuantity < state.product.allQuantity ? 1 : 0;
      state.hideShopTwoHours = !(
        isEquipment(state.product.part, state.product.actualQuantity) && state.product.price >= configProduct.minPriceShopTwoHours
      );
    },
    decrementQuantity: state => {
      state.product.actualQuantity -= state.product.actualQuantity > 1 ? 1 : 0;
      state.hideShopTwoHours = !(
        isEquipment(state.product.part, state.product.actualQuantity) && state.product.price >= configProduct.minPriceShopTwoHours
      );
    },
    hideTab: (state, action: PayloadAction<TabTitle[]>) => {
      const newTabs = [...state.tabs].filter(tab => !action.payload.includes(tab.title));
      if (!newTabs.some(x => x.title === state.activeTab)) {
        state.activeTab = newTabs[0].title;
      }
      state.tabs = newTabs;
    },
    sortTab: (
      state,
      action: PayloadAction<{
        array: TabType[];
        indexA: number;
        title: TabTitle;
      }>,
    ) => {
      const { array, indexA, title } = action.payload;
      state.tabs = sortTabs(array, indexA, title);
      state.activeTab = title;
    },
  },
  extraReducers: builder => {
    builder.addCase(HYDRATE, (state, action: any) => {
      return {
        ...state,
        ...action.payload.product,
      };
    });
    builder.addCase(getProductMainData.fulfilled, (state, action) => {
      const ListNoDuplicate = [];
      if (action.payload) {
        state.product = action.payload;
        const images = state.product.media.filter(item => item.type === 'image');
        const videos = state.product.media.filter(item => item.type === 'video');
        const sortedDataMedia: mediaType[] = [...images, ...videos];
        state.product.media = sortedDataMedia;

        state.tabs = generateTabs(action.payload.part);
        state.product.actualQuantity = getDefaultQuantityByType(action.payload.part, action.payload.allQuantity);
        state.hideShopTwoHours = !(
          isEquipment(action.payload.part, state.product.actualQuantity) && action.payload.price >= configProduct.minPriceShopTwoHours
        );
        if (action.payload.productSpecs) {
          const productSpecs = action.payload?.productSpecs
            .filter(x => x.value)
            .filter(x => (!ListNoDuplicate.includes(x.code) ? ListNoDuplicate.push(x.code) : false))
            .map(x => {
              return { ...x, code: x.code.toLowerCase() as ProductSpecCode };
            });

          const extraAttributes = attachFeatureToProduct(action.payload, productSpecs);

          state.product.productSpecs = [...productSpecs, ...extraAttributes];
        } else {
          state.product.productSpecs = [];
        }
      }
    });
    builder.addCase(getReviewProductData.fulfilled, (state, action) => {
      if (action.payload) {
        state.reviewProduct = action.payload;
      }
      state.isLoadedData = true;
    });
    builder.addCase(getReviewProductData.rejected, state => {
      state.isLoadedData = true;
    });
    builder.addCase(getCountReviews.fulfilled, (state, action) => {
      if (action.payload) {
        state.tabs.find(x => x.title === TabTitle.Reviews).quantity = action.payload;
      }
    });
  },
});

export const { actions: productActions } = productSlice;
